/** 通信录 Tab — 企业 (systemuser) + 客户 (contact) 两个子 tab */
import {
  ApartmentOutlined,
  MessageOutlined,
  PhoneOutlined,
  SearchOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Avatar,
  Button,
  Empty,
  Input,
  List,
  Space,
  Spin,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { type DirectoryContact, type DirectoryUser, sipPhoneApi } from '../../api/sip-phone-api.js';

interface Props {
  onDial: (number: string, name?: string) => void;
  onMessage: (peer: { userId?: string; name: string }) => void;
}

function pickExtensionForUser(u: DirectoryUser): string | null {
  return u.extension || null;
}

export function DirectoryTab({ onDial, onMessage }: Props) {
  const [q, setQ] = useState('');
  const [tab, setTab] = useState<'users' | 'contacts'>('users');
  const [users, setUsers] = useState<DirectoryUser[]>([]);
  const [contacts, setContacts] = useState<DirectoryContact[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const fetcher =
      tab === 'users'
        ? sipPhoneApi.listEnterprise(q || undefined)
        : sipPhoneApi.listContacts(q || undefined);
    fetcher
      .then((data) => {
        if (tab === 'users') setUsers(data as DirectoryUser[]);
        else setContacts(data as DirectoryContact[]);
      })
      .catch(() => message.error('加载通信录失败'))
      .finally(() => setLoading(false));
  }, [q, tab]);

  type Item = {
    key: string;
    avatar: React.ReactNode;
    title: string;
    subTitle: string;
    ext: string | null;
    kind: 'user' | 'contact';
    userId?: string;
  };
  const userItems: Item[] = useMemo(
    () =>
      users.map((u) => ({
        key: `u_${u.id}`,
        avatar: <Avatar style={{ background: '#1677ff' }}>{u.name?.[0] ?? '?'}</Avatar>,
        title: u.name,
        subTitle: [u.jobTitle, u.domainName].filter(Boolean).join(' · '),
        ext: pickExtensionForUser(u),
        kind: 'user',
        userId: u.id,
      })),
    [users],
  );
  const contactItems: Item[] = useMemo(
    () =>
      contacts.map((c) => ({
        key: `c_${c.id}`,
        avatar: <Avatar style={{ background: '#52c41a' }}>{c.name?.[0] ?? '?'}</Avatar>,
        title: c.name,
        subTitle: c.account?.name
          ? `${c.account.name}${c.jobTitle ? ` · ${c.jobTitle}` : ''}`
          : (c.jobTitle ?? ''),
        ext: c.mobile,
        kind: 'contact',
      })),
    [contacts],
  );

  return (
    <div>
      <Tabs
        size="small"
        activeKey={tab}
        onChange={(k) => setTab(k as 'users' | 'contacts')}
        items={[
          {
            key: 'users',
            label: (
              <span>
                <ApartmentOutlined /> 企业
              </span>
            ),
          },
          {
            key: 'contacts',
            label: (
              <span>
                <UserOutlined /> 客户
              </span>
            ),
          },
        ]}
      />
      <Input
        size="small"
        prefix={<SearchOutlined />}
        placeholder={tab === 'users' ? '搜索同事姓名 / 域账号' : '搜索联系人名/手机'}
        allowClear
        value={q}
        onChange={(e) => setQ(e.target.value)}
        style={{ marginBottom: 8 }}
      />

      <Spin spinning={loading}>
        {(tab === 'users' ? userItems : contactItems).length === 0 ? (
          <Empty description={tab === 'users' ? '暂无同事' : '暂无客户联系人'} />
        ) : (
          <List
            size="small"
            dataSource={tab === 'users' ? userItems : contactItems}
            style={{ maxHeight: 380, overflowY: 'auto' }}
            renderItem={(item) => (
              <List.Item
                style={{ padding: '6px 4px' }}
                actions={[
                  <Button
                    key="call"
                    type="text"
                    size="small"
                    icon={<PhoneOutlined style={{ color: '#1677ff' }} />}
                    disabled={!item.ext}
                    onClick={() => {
                      if (item.ext) onDial(item.ext, item.title);
                    }}
                  />,
                  item.kind === 'user' && item.userId ? (
                    <Button
                      key="msg"
                      type="text"
                      size="small"
                      icon={<MessageOutlined style={{ color: '#52c41a' }} />}
                      onClick={() => onMessage({ userId: item.userId, name: item.title })}
                    />
                  ) : null,
                ].filter(Boolean)}
              >
                <List.Item.Meta
                  avatar={item.avatar}
                  title={
                    <Space size={4}>
                      <Typography.Text style={{ fontSize: 13 }}>{item.title}</Typography.Text>
                      {item.ext && (
                        <Tag style={{ marginInlineEnd: 0, fontSize: 10 }} color="blue">
                          {item.ext}
                        </Tag>
                      )}
                    </Space>
                  }
                  description={
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      {item.subTitle || '—'}
                    </Typography.Text>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Spin>
    </div>
  );
}
